import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import type { SavedFilter, SchemaPage, ViewConfiguration } from '@/frontend/features/DashboardRuntime/types'
import { cn } from '@/lib/utils'

// The dashboard's contents live in the Workstation: pages grouped by chapter,
// topics of a chapter page as sub-items. The frame shows one page at a time.

export interface ChapterGroup { chapter: string | null; pages: SchemaPage[] }

export function chapterGroups(pages: SchemaPage[]): ChapterGroup[] {
  const groups: ChapterGroup[] = []
  for (const page of pages) {
    const chapter = page.chapter ?? null
    const last = groups[groups.length - 1]
    if (last && last.chapter === chapter) last.pages.push(page)
    else groups.push({ chapter, pages: [page] })
  }
  return groups
}

const pageLabel = (page: SchemaPage) => page.nav_title || page.title

export function DashboardContents({ pages, current, section, onSelect, copy }: {
  pages: SchemaPage[]; current: string | null; section?: string | null
  onSelect: (pageId: string, sectionId?: string | null) => void; copy: Record<string, string>
}) {
  return <nav aria-label={copy.contents} className="space-y-4 py-4 pl-3 pr-2 text-sm">
    {chapterGroups(pages).map((group, index) => <div key={`${group.chapter ?? ''}-${index}`}>
      {group.chapter && <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{group.chapter}</p>}
      <ul className="space-y-px">
        {group.pages.map(page => {
          const active = page.id === current
          return <li key={page.id}>
            <button type="button" aria-current={active ? 'page' : undefined} onClick={() => onSelect(page.id)}
              className={cn('w-full rounded-md px-2 py-1.5 text-left leading-snug transition-colors hover:bg-muted', active ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground')}>
              {pageLabel(page)}
            </button>
            {active && page.sections?.length ? <ul className="mb-1 ml-3 mt-0.5 space-y-px border-l pl-2">
              {page.sections.map(item => <li key={item.id}>
                <button type="button" onClick={() => onSelect(page.id, item.id)}
                  className={cn('w-full rounded px-2 py-1 text-left text-xs leading-snug transition-colors hover:bg-muted', section === item.id ? 'text-foreground' : 'text-muted-foreground')}>
                  {item.title}
                </button>
              </li>)}
            </ul> : null}
          </li>
        })}
      </ul>
    </div>)}
  </nav>
}

/** Narrow widths: the same contents as one grouped select. */
export function ContentsSelect({ pages, current, onSelect, copy }: { pages: SchemaPage[]; current: string | null; onSelect: (pageId: string) => void; copy: Record<string, string> }) {
  return <select aria-label={copy.contents_page} className="min-w-0 max-w-full flex-1 rounded-md border bg-background px-2 py-1.5 text-sm @4xl:hidden"
    value={current ?? ''} onChange={event => onSelect(event.target.value)}>
    {chapterGroups(pages).map((group, index) => group.chapter
      ? <optgroup key={`${group.chapter}-${index}`} label={group.chapter}>{group.pages.map(page => <option key={page.id} value={page.id}>{pageLabel(page)}</option>)}</optgroup>
      : group.pages.map(page => <option key={page.id} value={page.id}>{pageLabel(page)}</option>))}
  </select>
}

export function PageStepper({ pages, current, onSelect, copy }: { pages: SchemaPage[]; current: string | null; onSelect: (pageId: string) => void; copy: Record<string, string> }) {
  const index = pages.findIndex(page => page.id === current)
  const previous = index > 0 ? pages[index - 1] : null
  const next = index >= 0 && index < pages.length - 1 ? pages[index + 1] : null
  const button = 'inline-flex size-8 items-center justify-center rounded-md border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30'
  return <div className="flex shrink-0 items-center gap-1">
    <span className="hidden px-1 text-xs tabular-nums text-muted-foreground @xl:inline">{index + 1} / {pages.length}</span>
    <button type="button" className={button} disabled={!previous} onClick={() => previous && onSelect(previous.id)} aria-label={previous ? `${copy.previous_page}: ${pageLabel(previous)}` : copy.previous_page} title={previous ? pageLabel(previous) : undefined}><ChevronLeft className="size-4" /></button>
    <button type="button" className={button} disabled={!next} onClick={() => next && onSelect(next.id)} aria-label={next ? `${copy.next_page}: ${pageLabel(next)}` : copy.next_page} title={next ? pageLabel(next) : undefined}><ChevronRight className="size-4" /></button>
  </div>
}

/** The dashboard filters that apply to the page on screen, drawn by the Workstation above the frame. */
export function HostFilters({ specs, page, values, onChange, copy }: {
  specs: SavedFilter[]; page: string | null; values: ViewConfiguration['filters']; onChange: (values: ViewConfiguration['filters']) => void; copy: Record<string, string>
}) {
  const visible = specs.filter(spec => !spec.pages?.length || (page && spec.pages.includes(page)))
  if (!visible.length) return null
  const change = (spec: SavedFilter, value: string) => {
    const next = { ...values }
    if (!value) delete next[spec.name]
    else next[spec.name] = spec.type === 'number' ? Number(value) : value
    onChange(next)
  }
  const field = 'h-8 min-w-0 rounded-md border bg-background px-2 text-sm'
  return <div role="group" aria-label={copy.filters} className="flex min-w-0 flex-wrap items-center gap-2">
    {visible.map(spec => <label key={spec.name} className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <span className="shrink-0">{spec.label ?? spec.name}</span>
      {spec.options?.length
        ? <select className={cn(field, 'max-w-48')} value={String(values[spec.name] ?? spec.default ?? '')} onChange={event => change(spec, event.target.value)}>
          {!spec.options.includes(String(spec.default ?? '')) && <option value="">—</option>}
          {spec.options.map(option => <option key={option} value={option}>{spec.option_labels?.[option] ?? option}</option>)}
        </select>
        : <input type={spec.type === 'date' ? 'date' : spec.type === 'number' ? 'number' : 'text'} step={spec.type === 'number' ? 'any' : undefined}
          className={cn(field, 'w-36')} maxLength={500} value={String(values[spec.name] ?? spec.default ?? '')} onChange={event => change(spec, event.target.value)} />}
    </label>)}
  </div>
}


/** The contents without a column: the page on screen named in the toolbar, and one click opens every page grouped by
    chapter over the dashboard (with search); choosing a page closes it, so the dashboard keeps the full width. */
export function ContentsSwitcher({ pages, current, onSelect, copy }: {
  pages: SchemaPage[]; current: string | null; onSelect: (pageId: string, sectionId?: string | null) => void; copy: Record<string, string>
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const panel = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const page = pages.find(item => item.id === current) ?? pages[0]
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase()
    return chapterGroups(pages).map(group => ({ ...group, pages: q ? group.pages.filter(item => pageLabel(item).toLowerCase().includes(q) || (group.chapter ?? '').toLowerCase().includes(q)) : group.pages }))
      .filter(group => group.pages.length)
  }, [pages, query])
  useEffect(() => {
    if (!open) return undefined
    const close = (event: MouseEvent) => { if (!panel.current?.contains(event.target as Node) && !button.current?.contains(event.target as Node)) setOpen(false) }
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); button.current?.focus() } }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', escape) }
  }, [open])
  const choose = (id: string) => { setOpen(false); setQuery(''); onSelect(id) }
  return <div className="relative min-w-0 flex-1">
    <button ref={button} type="button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(!open)}
      className="flex max-w-full items-center gap-2 rounded-md px-2 py-1 text-left transition-colors hover:bg-muted">
      <span className="min-w-0 truncate text-sm"><span className="text-muted-foreground">{page?.chapter ? `${page.chapter} · ` : ''}</span><span className="font-medium">{page ? pageLabel(page) : ''}</span></span>
      <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
    </button>
    {open && <div ref={panel} role="dialog" aria-label={copy.contents}
      className="absolute left-0 top-full z-30 mt-1 w-[min(920px,calc(100vw-2rem))] rounded-lg border bg-popover p-3 shadow-lg">
      <label className="mb-3 flex items-center gap-2 rounded-md border px-2"><Search className="size-4 text-muted-foreground" />
        <input autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder={copy.contents_search ?? 'Buscar página'}
          className="h-8 w-full bg-transparent text-sm outline-none" /></label>
      <div className="grid max-h-[60vh] gap-x-6 gap-y-4 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
        {groups.map((group, index) => <div key={`${group.chapter ?? ''}-${index}`} className="min-w-0">
          {group.chapter && <p className="mb-1 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{group.chapter}</p>}
          <ul className="space-y-px">{group.pages.map(item => <li key={item.id}>
            <button type="button" aria-current={item.id === current ? 'page' : undefined} onClick={() => choose(item.id)}
              className={cn('w-full rounded-md px-2 py-1.5 text-left text-sm leading-snug transition-colors hover:bg-muted', item.id === current ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground')}>
              {pageLabel(item)}
            </button>
          </li>)}</ul>
        </div>)}
      </div>
    </div>}
  </div>
}

/** Left and right arrows walk the pages when focus is not in a field. */
export function usePageKeys(pages: SchemaPage[], current: string | null, onSelect: (pageId: string) => void) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (event.altKey || event.ctrlKey || event.metaKey || (target && /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) || target?.isContentEditable) return
      const index = pages.findIndex(page => page.id === current)
      if (event.key === 'ArrowRight' && index >= 0 && index < pages.length - 1) onSelect(pages[index + 1].id)
      if (event.key === 'ArrowLeft' && index > 0) onSelect(pages[index - 1].id)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pages, current, onSelect])
}
